import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiMailLine, RiUserAddLine } from '@remixicon/react';
import { toast } from 'sonner';
import { TEAM_ROLES } from '@/constants';

const Field = ({ label, required, children, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const AddMemberDrawer = ({ open, onClose, onSubmit, onViewPermissions, initialValues = null }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = !!initialValues;

  useEffect(() => {
    if (open) {
      setFirstName(initialValues?.firstName || '');
      setLastName(initialValues?.lastName || '');
      setEmail(initialValues?.email || '');
      setRole(initialValues?.role || '');
      setSaving(false);
    }
  }, [
    open,
    initialValues?.firstName,
    initialValues?.lastName,
    initialValues?.email,
    initialValues?.role,
  ]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit =
    firstName.trim() && lastName.trim() && emailValid && role && !saving;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = { firstName, lastName, email, role };
      await onSubmit?.(payload, initialValues);
      onClose();
      toast.success(isEdit ? 'Member updated' : 'Invitation sent', { position: 'bottom-right' });
    } catch (err) {
      toast.error(
        err?.message || (isEdit ? 'Failed to update member' : 'Failed to add member'),
        { position: 'bottom-right' }
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[9998] bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-[9999] w-full sm:w-[560px] lg:w-[640px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4">
          <h3 className="text-lg font-semibold text-primary-700">
            {isEdit ? 'Edit member' : 'Add new member'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" required>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </Field>
            <Field label="Last Name" required>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </Field>
          </div>

          <Field label="Email Address" required>
            <div className="relative flex items-center">
              <RiMailLine className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                readOnly={isEdit}
                className={`w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  isEdit ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
                }`}
              />
            </div>
          </Field>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Role<span className="text-red-500 ml-0.5">*</span>
              </label>
              {onViewPermissions && (
                <button
                  type="button"
                  onClick={onViewPermissions}
                  className="text-sm font-medium text-primary-700 hover:underline"
                >
                  View Permissions
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TEAM_ROLES.map((r) => {
                const selected = role === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${
                      selected
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span>{r.label}</span>
                    <span
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        selected ? 'border-primary-600' : 'border-gray-300'
                      }`}
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-primary-600" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
              canSubmit
                ? 'bg-primary-700 text-white hover:bg-primary-800'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <RiUserAddLine className="w-4 h-4" />
            {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Update Member' : 'Add Member')}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AddMemberDrawer;
